import React, { useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, TextField, Typography, Link, CircularProgress } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  AuthLayout, Eyebrow, Heading, Notice, PrimaryButton, fieldSx,
} from "./authShared";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();

  // Sign-up already navigates here with the address in router state. The old
  // page ignored it and showed an empty field, so people had to retype the one
  // value that has to match exactly for the code to be found.
  const [email, setEmail] = useState(location.state?.email || "");
  const [code, setCode]   = useState("");
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk]       = useState(location.state?.notice || "");

  const handleVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");

    try {
      const res = await axios.post(
        "/api/verify-email",
        { email, code },
        { withCredentials: true, timeout: 10000 }
      );

      setOk(res.data.message || "Email verified. Taking you to sign in…");
      setTimeout(() => navigate("/login", { replace: true }), 900);
    } catch (err) {
      console.error("Verify email error:", err);
      setError(err.response?.data?.message || "Could not verify that code.");
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      panel={{
        eyebrow: "Almost there",
        heading: "Confirm it's you.",
        body: "We sent a six digit code to your inbox. Entering it activates your account and unlocks every destination.",
      }}
    >
      <Eyebrow>Step 2 of 2</Eyebrow>
      <Heading>Verify email</Heading>

      <Typography sx={{ color: "var(--fn-muted-l)", fontSize: 16, mb: 4 }}>
        Enter the 6 digit code we sent you.
      </Typography>

      {error && <Notice onClose={() => setError("")}>{error}</Notice>}
      {!error && ok && <Notice severity="success" onClose={() => setOk("")}>{ok}</Notice>}

      <Box component="form" onSubmit={handleVerify} noValidate>
        <TextField
          fullWidth required
          label="Email address"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
          sx={{ ...fieldSx, mb: 2 }}
        />

        {/* Mono, spaced and centred: a code is read back character by character,
            and a proportional face makes 0/O and 1/l harder to tell apart.
            inputMode="numeric" brings up the number pad on a phone. */}
        <TextField
          fullWidth required
          label="6 digit code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          disabled={busy}
          autoFocus={Boolean(email)}
          inputProps={{
            inputMode: "numeric",
            autoComplete: "one-time-code",
            maxLength: 6,
            style: {
              fontFamily: "var(--fn-mono)",
              fontSize: 26,
              letterSpacing: ".34em",
              textAlign: "center",
              paddingLeft: ".34em",
            },
          }}
          sx={fieldSx}
        />

        <PrimaryButton
          type="submit"
          disabled={busy || code.length < 6}
          endIcon={!busy && <ArrowForwardIcon />}
          sx={{ mt: 3 }}
        >
          {busy
            ? <CircularProgress size={21} thickness={5} sx={{ color: "var(--fn-cream)" }} />
            : "Verify"}
        </PrimaryButton>

        <Box sx={{ mt: 3, textAlign: "center" }}>
          <Link
            href="/login"
            sx={{
              fontSize: 14, fontWeight: 600, color: "var(--fn-muted-l)",
              textDecoration: "none",
              "&:hover": { color: "var(--fn-teal-ink)", textDecoration: "underline" },
            }}
          >
            Back to sign in
          </Link>
        </Box>
      </Box>
    </AuthLayout>
  );
}
